import { z } from 'zod'

/**
 * GTD Lite workflow schemas using Zod
 */
export const InboxItemSchema = z.object({
  id: z.string().describe('Unique identifier for the inbox item'),
  title: z.string().describe('Short descriptive title of the item'),
  description: z.string().describe('Detailed content of the item'),
  context: z.string().optional().describe('Optional situational context'),
  actionability: z
    .enum(['actionable', 'reference', 'someday', 'delegated'])
    .optional()
    .describe('GTD classification'),
  nextSteps: z.array(z.string()).optional().describe('Suggested next steps'),
  status: z
    .enum(['new', 'in-progress', 'done', 'blocked'])
    .default('new')
    .describe('Current workflow status'),
  owner: z.string().optional().describe('Responsible person'),
  due: z.string().optional().describe('Optional due date ISO string'),
  processed: z.boolean().default(false).describe('Whether reviewed'),
  createdAt: z.string().describe('Creation timestamp ISO string'),
  updatedAt: z.string().describe('Last-modified timestamp ISO string'),
  assignedTo: z.string().optional().describe("Party ID of the developer assigned to this task"),
  roleType: z.string().optional().describe("Role type of the assigned developer"),
  assignedAt: z.string().optional().describe("Timestamp when the task was assigned"),
})

export type InboxItem = z.infer<typeof InboxItemSchema>
export type InboxItemModel = InboxItem

export const TaskSchema = z.object({
  id: z.string().describe('Unique identifier for the task'),
  title: z.string().describe('Task title'),
  context: z.array(z.string()).default([]).describe('Tags for categorization'),
  priority: z.enum(['high', 'medium', 'low']).default('medium').describe('Priority level'),
  duration: z.number().optional().describe('Estimated minutes'),
  status: z.enum(['new', 'in-progress', 'done', 'blocked']).default('new').describe('Task status'),
  inboxItemId: z.string().optional().describe('Origin inbox item ID'),
  due: z.string().optional().describe('Due date ISO string'),
  createdAt: z.string().describe('Creation timestamp ISO string'),
  updatedAt: z.string().describe('Last-modified timestamp ISO string'),
  assignedTo: z.string().optional().describe("Party ID of the developer assigned to this task"),
  roleType: z.string().optional().describe("Role type of the assigned developer"),
  assignedAt: z.string().optional().describe("Timestamp when the task was assigned"),
  completedAt: z.string().optional().describe("Timestamp when the task was completed"),
  productId: z.string().optional().describe("Product ID this task belongs to"),
  // Blocking support for task dependencies
  blockedReason: z.string().optional().describe("Reason why the task is blocked"),
  blockedAt: z.string().optional().describe("Timestamp when the task was blocked"),
})

export type Task = z.infer<typeof TaskSchema>

export const ProjectSchema = z.object({
  id: z.string().describe('Unique identifier for the project'),
  title: z.string().describe('Project title'),
  description: z.string().optional().describe('Project details'),
  context: z.array(z.string()).default([]).describe('Project tags'),
  tasks: z.array(z.string()).describe('List of task IDs'),
  status: z.enum(['planning', 'active', 'on-hold', 'completed']).default('planning').describe('Project status'),
  inboxItemId: z.string().optional().describe('Origin inbox item ID'),
  due: z.string().optional().describe('Due date ISO string'),
  progress: z.number().default(0).describe('Completion percentage'),
  createdAt: z.string().describe('Creation timestamp ISO string'),
  updatedAt: z.string().describe('Last-modified timestamp ISO string'),
  assignedTo: z.string().optional().describe("Party ID of the developer assigned to this task"),
  roleType: z.string().optional().describe("Role type of the assigned developer"),
  assignedAt: z.string().optional().describe("Timestamp when the task was assigned"),
})

export type Project = z.infer<typeof ProjectSchema>

export const ContextSchema = z.object({
  id: z.string().describe('Unique context/tag ID'),
  name: z.string().describe('Context display name'),
  description: z.string().optional().describe('Description of usage'),
  color: z.string().optional().describe('UI color code'),
})

export type Context = z.infer<typeof ContextSchema>

export const ProgressUpdateSchema = z.object({
  projectId: z.string().optional().describe('Project ID'),
  taskId: z.string().optional().describe('Task ID'),
  progress: z.number().describe('Progress percentage'),
  status: z.enum(['on-track', 'at-risk', 'blocked']).default('on-track').describe('Health status'),
  blockers: z.array(z.string()).optional().describe('Blocker descriptions'),
  timestamp: z.string().describe('Update timestamp ISO string'),
})

export type ProgressUpdate = z.infer<typeof ProgressUpdateSchema>

export const TodayItemSchema = z.object({
  id: z.string().describe('Identifier for today item'),
  taskId: z.string().describe('Associated task ID'),
  priority: z.enum(['high', 'medium', 'low']).describe('Today priority'),
  scheduledTime: z.string().optional().describe('Scheduled time ISO string'),
  completed: z.boolean().default(false).describe('Completion flag'),
  date: z.string().describe('Date ISO string'),
})

export type TodayItem = z.infer<typeof TodayItemSchema>

export const NudgeSchema = z.object({
  id: z.string().describe('Unique nudge ID'),
  taskId: z.string().describe('Associated task ID'),
  projectId: z.string().optional().describe('Associated project ID'),
  message: z.string().describe('Nudge message'),
  type: z.enum(['reminder', 'suggestion', 'progress', 'blocked']).describe('Nudge type'),
  dismissed: z.boolean().default(false).describe('Dismissed flag'),
  timestamp: z.string().describe('Creation timestamp ISO string'),
})

export type Nudge = z.infer<typeof NudgeSchema>

export const NextActionSchema = z.object({
  id: z.string(),
  text: z.string(),
  done: z.boolean(),
  category: z.string(),
  fileName: z.string(),
  fileCreatedAt: z.string(),
  fileModifiedAt: z.string(),
  fileSize: z.number(),
  lineNumber: z.number(),
})

export type NextAction = z.infer<typeof NextActionSchema>
